import React, { useState } from "react";

const GetItemInfo = () => {
  const [folderId, setFolderId] = useState("");
  const [data, setData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [savingVideos, setSavingVideos] = useState(false);

  const [error, setError] = useState(null);

  const [selectedFields, setSelectedFields] = useState({});
  const [updatedFields, setUpdatedFields] = useState({});

  const [videoUrls, setVideoUrls] = useState([]);

  const fetchFolderInfo = async () => {
    const cleanFolderId = folderId.trim();

    if (!cleanFolderId) {
      setError("Please enter a folder ID.");
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    setSelectedFields({});
    setUpdatedFields({});
    setVideoUrls([]);

    try {
      const response = await fetch(
        `https://api.malidag.com/info/${encodeURIComponent(cleanFolderId)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch folder information.");
      }

      const result = await response.json();

      const textFields = {
        itemSubName: result?.textFields?.itemSubName || "",
        ...(result?.textFields ?? {}),
      };

      setData({
        ...result,
        textFields,
      });

      const initialFields = {};

      Object.keys(textFields).forEach((key) => {
        initialFields[key] = false;
      });

      setSelectedFields(initialFields);
      setUpdatedFields(textFields);

      setVideoUrls(
        Array.isArray(result?.video)
          ? result.video.map((url) => String(url || ""))
          : []
      );
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setUpdatedFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFieldSelection = (field) => {
    setSelectedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getInputValue = (field) => {
    const value = updatedFields[field];

    if (field === "sizes" && typeof value === "object") {
      return JSON.stringify(value);
    }

    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const handleSubmit = async () => {
    const fieldsToUpdate = Object.keys(selectedFields || {}).reduce(
      (acc, field) => {
        if (!selectedFields[field]) {
          return acc;
        }

        let value = updatedFields[field];

        if (field === "sizes" && typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch {
            alert("Sizes must contain valid JSON.");
            throw new Error("Invalid sizes JSON");
          }
        }

        acc[field] = value;

        return acc;
      },
      {}
    );

    if (!Object.keys(fieldsToUpdate).length) {
      alert("Select at least one field to update.");
      return;
    }

    try {
      setSavingFields(true);

      const response = await fetch(
        `https://api.malidag.com/modify/${encodeURIComponent(folderId.trim())}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(fieldsToUpdate),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update fields.");
      }

      alert("Fields updated successfully!");

      setSelectedFields((prev) =>
        Object.keys(prev).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {})
      );
    } catch (err) {
      if (err.message !== "Invalid sizes JSON") {
        alert(
          "Error updating fields: " +
            (err.message || "Unknown error")
        );
      }
    } finally {
      setSavingFields(false);
    }
  };

  const handleVideoChange = (index, value) => {
    setVideoUrls((prev) =>
      prev.map((url, i) => (i === index ? value : url))
    );
  };

  const addVideoUrl = () => {
    setVideoUrls((prev) => [...prev, ""]);
  };

  const removeVideoUrl = (index) => {
    setVideoUrls((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const removeAllVideos = () => {
    const confirmed = window.confirm(
      "Remove every video URL from this product?"
    );

    if (!confirmed) return;

    setVideoUrls([]);
  };

  const saveVideos = async () => {
    const cleanedVideos = videoUrls
      .map((url) => String(url || "").trim())
      .filter(Boolean);

    try {
      setSavingVideos(true);

      const response = await fetch(
        `https://api.malidag.com/modify-video/${encodeURIComponent(
          folderId.trim()
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            video: cleanedVideos,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.message || "Failed to update video URLs."
        );
      }

      setVideoUrls(result.video || []);

      setData((prev) => ({
        ...prev,
        video: result.video || [],
      }));

      alert("Video URLs updated successfully!");
    } catch (err) {
      alert(
        "Error updating videos: " +
          (err.message || "Unknown error")
      );
    } finally {
      setSavingVideos(false);
    }
  };

  const textFields = {
    itemSubName: data?.textFields?.itemSubName || "",
    ...(data?.textFields ?? {}),
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "30px auto",
        padding: "20px",
      }}
    >
      <h1>Product Information Editor</h1>

      {/* SEARCH */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "30px",
        }}
      >
        <input
          type="text"
          placeholder="Enter Folder ID"
          value={folderId}
          onChange={(e) => setFolderId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              fetchFolderInfo();
            }
          }}
          style={{
            flex: 1,
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid #ccc",
          }}
        />

        <button
          type="button"
          onClick={fetchFolderInfo}
          disabled={loading}
          style={{
            padding: "12px 20px",
            backgroundColor: "#111827",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          {loading ? "Loading..." : "Get Product"}
        </button>
      </div>

      {error && (
        <div
          style={{
            color: "#b91c1c",
            marginBottom: "20px",
          }}
        >
          {error}
        </div>
      )}

      {data && (
        <>
          <div
            style={{
              marginBottom: "30px",
              paddingBottom: "20px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
              }}
            >
              FOLDER ID
            </div>

            <strong>{data.folderId}</strong>
          </div>

          {/* IMAGES */}
          <section
            style={{
              marginBottom: "40px",
            }}
          >
            <h2>Images</h2>

            {!data.images?.length ? (
              <p>No images.</p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: "12px",
                }}
              >
                {data.images.map((image, index) => (
                  <div
                    key={`${image}-${index}`}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={image}
                      alt={`Product ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "130px",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* VIDEO EDITOR */}
          <section
            style={{
              marginBottom: "40px",
              padding: "20px",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                  }}
                >
                  Video URLs
                </h2>

                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    marginTop: "5px",
                  }}
                >
                  {videoUrls.length} video URL
                  {videoUrls.length === 1 ? "" : "s"}
                </div>
              </div>

              <button
                type="button"
                onClick={addVideoUrl}
                style={{
                  padding: "9px 14px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#fff",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                + Add URL
              </button>
            </div>

            {videoUrls.length === 0 ? (
              <div
                style={{
                  padding: "25px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  textAlign: "center",
                  color: "#6b7280",
                }}
              >
                No video attached to this product.
              </div>
            ) : (
              videoUrls.map((url, index) => (
                <div
                  key={index}
                  style={{
                    padding: "15px 0",
                    borderBottom:
                      index < videoUrls.length - 1
                        ? "1px solid #e5e7eb"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="text"
                      value={url}
                      onChange={(e) =>
                        handleVideoChange(
                          index,
                          e.target.value
                        )
                      }
                      placeholder="https://..."
                      style={{
                        flex: 1,
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                      }}
                    />

                    <button
                      type="button"
                      onClick={() =>
                        removeVideoUrl(index)
                      }
                      style={{
                        padding: "10px 14px",
                        border: "1px solid #fecaca",
                        background: "#fff",
                        color: "#dc2626",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  {url.trim() && (
                    <div
                      style={{
                        marginTop: "12px",
                      }}
                    >
                      <video
                        src={url}
                        controls
                        preload="metadata"
                        style={{
                          width: "100%",
                          maxWidth: "420px",
                          maxHeight: "300px",
                          background: "#000",
                          borderRadius: "10px",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display =
                            "none";
                        }}
                      />
                    </div>
                  )}
                </div>
              ))
            )}

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "20px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={saveVideos}
                disabled={savingVideos}
                style={{
                  padding: "11px 18px",
                  background: "#111827",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "700",
                }}
              >
                {savingVideos
                  ? "Saving..."
                  : "Save Video URLs"}
              </button>

              {videoUrls.length > 0 && (
                <button
                  type="button"
                  onClick={removeAllVideos}
                  style={{
                    padding: "11px 18px",
                    background: "#fff",
                    color: "#dc2626",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Remove All
                </button>
              )}
            </div>
          </section>

          {/* TEXT FIELDS */}
          <section>
            <h2>Product Fields</h2>

            {Object.keys(textFields).length === 0 ? (
              <div>No text fields available.</div>
            ) : (
              <div>
                {Object.keys(textFields).map((field) => (
                  <div
                    key={field}
                    style={{
                      padding: "14px 0",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          !!selectedFields[field]
                        }
                        onChange={() =>
                          handleFieldSelection(field)
                        }
                      />

                      <label
                        style={{
                          fontWeight: "700",
                        }}
                      >
                        {field}
                      </label>
                    </div>

                    <input
                      type="text"
                      value={getInputValue(field)}
                      onChange={(e) =>
                        handleFieldChange(
                          field,
                          e.target.value
                        )
                      }
                      disabled={
                        !selectedFields[field]
                      }
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        background: selectedFields[field]
                          ? "#fff"
                          : "#f9fafb",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                Object.keys(textFields).length === 0 ||
                savingFields
              }
              style={{
                marginTop: "20px",
                padding: "12px 18px",
                backgroundColor: "#111827",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "700",
              }}
            >
              {savingFields
                ? "Saving..."
                : "Submit Selected Changes"}
            </button>
          </section>
        </>
      )}
    </div>
  );
};

export default GetItemInfo;